import api from "./api";

export type CondicaoPagamentoAvaliacao = {
  id?: number;
  titulo?: string | null;
  codigo?: string | null;
  dias?: number | null;
  selecionado?: boolean | null;
};

export type TituloDeducaoAvaliacao = {
  id?: number;
  filial?: string | null;
  titulo?: string | null;
  parcela?: string | number | null;
  natureza?: string | null;
  emitidoEm?: string | null;
  vencimentoEm?: string | null;
  diasAtraso?: number | null;
  valor?: number | null;
  historico?: string | null;
  status?: string | null;
};

export type OutraDeducaoAvaliacao = {
  id?: number;
  descricao?: string | null;
  valor?: number | null;
};

export type PagamentoComprovanteDespesasResponse = {
  idPagamento?: number | null;
  idComprovanteDespesas?: number | null;
  sindicato?: {
    nome?: string | null;
    cnpj?: string | null;
    sigla?: string | null;
  } | null;
  valores?: {
    valorBruto?: number | null;
    valorDeducoes?: number | null;
    valorTotalLiquido?: number | null;
  } | null;
  titulosDeducoes?: TituloDeducaoAvaliacao[] | null;
  outrasDeducoes?: OutraDeducaoAvaliacao[] | null;
  dadosPagamento?: {
    descricao?: string | null;
    emitidoEm?: string | null;
    vencimentoEm?: string | null;
    numeroPag?: string | null;
    aprovadoEm?: string | null;
    numeroTitulo?: string | null;
    condicaoPagamentoId?: number | null;
    condicoesPagamento?: CondicaoPagamentoAvaliacao[] | null;
  } | null;
};

export type PagamentoSolicitacaoListItem = {
  comprovanteId?: number | null;
  pagamentoId?: number | null;
  idPagamento?: number | null;
  pagamentoDespesasAvaliacaoId?: number | null;
  sindicatoId?: number | null;
  cnpj?: string | null;
  sigla?: string | null;
  nome?: string | null;
  situacao?: {
    id?: number | null;
    nome?: string | null;
  } | string | null;
  solicitacao?: number | string | null;
  assinadoEm?: string | null;
  emitidoEm?: string | null;
  valorBruto?: number | null;
};

export type PagamentoEventoItem = {
  id?: number | null;
  nome?: string | null;
  criadoEm?: string | null;
  arquivoId?: number | null;
};

export type PagamentoSolicitacoesListResponse = {
  anoReferenciaSelecionado?: number | null;
  avaliacoesModelo?: {
    anoReferencia?: number | null;
    ativo?: boolean | null;
  }[] | null;
  pagamentos?: PagamentoSolicitacaoListItem[] | null;
};

export type GerarPagAvaliacaoPayload = {
  condicaoPagamentoId: number;
};

// Endpoint: GET /api/pagamentos-despesas-avaliacoes/solicitacoes?anoReferencia=ANO
export const getPagamentoSolicitacoesByAno = async (
  anoReferencia: number,
): Promise<PagamentoSolicitacoesListResponse> => {
  try {
    const resp = await api.get(
      "/api/pagamentos-despesas-avaliacoes/solicitacoes",
      { params: { anoReferencia } },
    );
    return resp?.data || {};
  } catch (e) {
    console.error("Erro ao buscar lista de pagamento:", e);
    throw e;
  }
};

const parseEventosPayload = (payload: unknown): PagamentoEventoItem[] => {
  if (Array.isArray(payload)) return payload as PagamentoEventoItem[];
  if (payload && typeof payload === "object") {
    const nested =
      (payload as { eventos?: PagamentoEventoItem[] }).eventos ??
      (payload as { data?: PagamentoEventoItem[] }).data;
    if (Array.isArray(nested)) return nested;
  }
  return [];
};

// Endpoint: GET /api/pagamentos-despesas-avaliacoes/{PagamentoDespesasAvaliacaoId}/eventos
export const getPagamentoEventosById = async (
  pagamentoDespesasAvaliacaoId: number,
): Promise<PagamentoEventoItem[]> => {
  if (!pagamentoDespesasAvaliacaoId) return [];

  try {
    const resp = await api.get(
      `/api/pagamentos-despesas-avaliacoes/${pagamentoDespesasAvaliacaoId}/eventos`,
    );
    return parseEventosPayload(resp?.data);
  } catch (e) {
    console.error("Erro ao buscar histórico de pagamento:", e);
    throw e;
  }
};

// Endpoint: GET /api/pagamentos-despesas-avaliacoes/comprovantes-despesas/{comprovanteDespesasId}
export const getPagamentoComprovanteDespesasById = async (
  comprovanteDespesasId: number,
): Promise<PagamentoComprovanteDespesasResponse> => {
  try {
    const resp = await api.get(
      `/api/pagamentos-despesas-avaliacoes/comprovantes-despesas/${comprovanteDespesasId}`,
    );
    return resp?.data || {};
  } catch (e) {
    console.error("Erro ao buscar programação de pagamento:", e);
    throw e;
  }
};

// Endpoint: POST /api/pagamentos-despesas-avaliacoes/{pagamentoId}/gerar-pag
export const gerarPagPagamentoDespesasAvaliacao = async (
  pagamentoId: number,
  payload: GerarPagAvaliacaoPayload,
) => {
  try {
    const corpo = {
      condicaoPagamentoId: Number(payload.condicaoPagamentoId),
    };
    const resp = await api.post(
      `/api/pagamentos-despesas-avaliacoes/${pagamentoId}/gerar-pag`,
      corpo,
    );
    return resp?.data ?? true;
  } catch (e: unknown) {
    const err = e as { response?: { status?: number; data?: unknown } };
    console.error(
      "Erro ao gerar PAG:",
      err?.response?.status,
      err?.response?.data || e,
    );
    throw e;
  }
};

const filenameFromContentDisposition = (cd = "") => {
  if (!cd) return "";
  const star = cd.match(/filename\*\s*=\s*([^']*)''([^;]+)/i);
  if (star?.[2]) {
    try {
      return decodeURIComponent(star[2].trim());
    } catch {
      return star[2].trim();
    }
  }
  const simple = cd.match(/filename\s*=\s*"([^"]+)"|filename\s*=\s*([^;]+)/i);
  return (simple?.[1] || simple?.[2] || "").trim().replace(/^"(.*)"$/, "$1");
};

// Endpoint: GET /api/pagamentos-despesas-avaliacoes/{arquivoId}/arquivo-evento
export const downloadPagamentoArquivoEvento = async (
  arquivoId: number,
  nomeArquivo = "pagamento_evento.pdf",
) => {
  if (!arquivoId) {
    throw new Error("arquivoId é obrigatório.");
  }

  try {
    const resp = await api.get(
      `/api/pagamentos-despesas-avaliacoes/${arquivoId}/arquivo-evento`,
      { responseType: "blob" },
    );

    const contentType = String(resp.headers?.["content-type"] || "");
    const blob = new Blob([resp.data], {
      type: contentType || "application/octet-stream",
    });
    const url = window.URL.createObjectURL(blob);
    const filename =
      filenameFromContentDisposition(resp.headers?.["content-disposition"]) ||
      nomeArquivo;

    const link = document.createElement("a");
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    link.remove();
    setTimeout(() => URL.revokeObjectURL(url), 0);
  } catch (e) {
    console.error("Erro ao baixar arquivo do histórico de pagamento:", e);
    throw e;
  }
};

// Endpoint: GET /api/pagamentos-despesas-avaliacoes/{pagamentoId}/comprovante-despesas/arquivo-assinado
export const downloadPagamentoComprovanteDespesasArquivoAssinado = async (
  pagamentoId: number,
  nomeArquivo = "comprovante_despesas_assinado.pdf",
) => {
  if (!pagamentoId) {
    throw new Error("pagamentoId é obrigatório.");
  }

  try {
    const resp = await api.get(
      `/api/pagamentos-despesas-avaliacoes/${pagamentoId}/comprovante-despesas/arquivo-assinado`,
      { responseType: "blob" },
    );

    const contentType = String(resp.headers?.["content-type"] || "");
    const blob = new Blob([resp.data], {
      type: contentType || "application/octet-stream",
    });
    const url = window.URL.createObjectURL(blob);
    const filename =
      filenameFromContentDisposition(resp.headers?.["content-disposition"]) ||
      nomeArquivo;

    const link = document.createElement("a");
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    link.remove();
    setTimeout(() => URL.revokeObjectURL(url), 0);
  } catch (e) {
    console.error("Erro ao baixar formulário de comprovante assinado:", e);
    throw e;
  }
};
